// ** React Imports
import { useLayoutEffect, useRef } from 'react'

// ** Icon Imports
import { Lock, Sparkles } from 'lucide-react'

// ** Component Imports
import { FreePlanUpgradeButton } from './FreePlanUpgradeButton'

// ** Utility Imports
import {
	FREE_PLAN_ENDED_MESSAGE,
	isFreePlanEnded,
	isOnFreePlan
} from '@/lib/access'
import { useAppSelector } from '@/shared/lib'

/**
 * The vertical space the banner takes (height + bottom margin), published on
 * `:root` so full-height screens (e.g. the Agent Lara chat) can subtract it.
 * `0px` whenever the banner is not shown.
 */
export const FREE_PLAN_BANNER_SPACE_VAR = '--free-plan-banner-space'

const BANNER_CLASSES =
	'mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-2 text-sm text-ink'

const formatDaysLeft = (daysLeft: number): string =>
	`${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`

/**
 * Slim app-wide banner for the free plan: usage while it runs, and the
 * "ended" notice once the 90 days are over (the app's access guards already
 * show their subscribe prompts then). Renders nothing for subscribers.
 */
export const FreePlanBanner = () => {
	const { user: currentUser } = useAppSelector((state) => state.currentUser)
	const bannerRef = useRef<HTMLDivElement>(null)

	const freePlan = currentUser?.freePlan
	const hasEnded = isFreePlanEnded(currentUser)
	const isVisible = hasEnded || (isOnFreePlan(currentUser) && !!freePlan)

	// keeps the published space in sync, including when the banner wraps
	useLayoutEffect(() => {
		const root = document.documentElement
		const banner = bannerRef.current

		if (!banner) {
			root.style.setProperty(FREE_PLAN_BANNER_SPACE_VAR, '0px')
			return
		}

		const publishSpace = () => {
			const { marginBottom } = getComputedStyle(banner)
			const space = banner.offsetHeight + parseFloat(marginBottom)
			root.style.setProperty(FREE_PLAN_BANNER_SPACE_VAR, `${space}px`)
		}

		publishSpace()
		const observer = new ResizeObserver(publishSpace)
		observer.observe(banner)

		return () => {
			observer.disconnect()
			root.style.setProperty(FREE_PLAN_BANNER_SPACE_VAR, '0px')
		}
	}, [isVisible])

	if (!isVisible) return null

	if (hasEnded) {
		return (
			<div
				ref={bannerRef}
				className={`${BANNER_CLASSES} border-warning bg-warning-soft`}
				role='status'
			>
				<p className='flex items-center gap-2'>
					<Lock className='size-4 shrink-0' />
					<span>
						{FREE_PLAN_ENDED_MESSAGE} Your data is kept and reopens as soon as
						you subscribe.
					</span>
				</p>

				<FreePlanUpgradeButton />
			</div>
		)
	}

	const parts = [
		freePlan?.daysLeft !== null && freePlan?.daysLeft !== undefined
			? formatDaysLeft(freePlan.daysLeft)
			: null,
		`${freePlan?.laraMessagesUsed ?? 0}/${
			freePlan?.laraMessagesLimit ?? 0
		} Agent Lara messages used`
	].filter((part): part is string => part !== null)

	return (
		<div
			ref={bannerRef}
			className={`${BANNER_CLASSES} border-primary-bg bg-primary-bg-20`}
		>
			<p className='flex items-center gap-2'>
				<Sparkles className='size-4 shrink-0 text-primary' />
				<span>
					<span className='font-semibold'>Free plan:</span> {parts.join(' · ')}
				</span>
			</p>

			<FreePlanUpgradeButton />
		</div>
	)
}
